import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box, styled, useTheme } from '@mui/material';
import { IconExternalLink } from '@tabler/icons-react';

import {
  Button,
  Drawer,
  Skeleton,
  Typography,
} from '@zendesk/forethought-elements';
import { NO_SELECTED_INSTRUCTION } from '../knowledge/constants';
import { AiInstructionDetail } from '../workflow-builder-edit/customization-panel/ai-instructions/AiInstructionDetail';
import { AuditDrawerBody } from './ConversationDrawer/audit/AuditDrawerBody';
import { useIsAuditDrawerEnabled } from './ConversationDrawer/audit/useIsAuditDrawerEnabled';
import { FeedbackPanel } from './ConversationDrawer/FeedbackPanel';
import { RevampDrawerBody } from './ConversationDrawer/RevampConversationDrawer';
import { VoiceDrawerBody } from './voice-conversation-drawer/VoiceDrawerBody';
import { NA } from './constants';
import DiscoverAutomationCheck from 'src/assets/images/discover-automation-check-v2.svg';
import { useEmitTrackingEventCallback } from 'src/hooks/hooks';
import { usePublishInstructionMutation } from 'src/services/ai-instruction/aiInstructionApi';
import { Conversation } from 'src/services/apiInterfaces';
import { ConversationReviewRating } from 'src/services/insights/types';

const CONVERSATION_DRAWER_PUBLISHED_INSTRUCTION_CACHE_KEY =
  'conversation-drawer-published-instruction';

export default function ConversationDrawer({
  flaggedConversationIds = [],
  isLoading = false,
  onClose,
  onSelectConversation,
  openEventTrigger,
  selectedConversation,
}: {
  flaggedConversationIds?: string[];
  isLoading?: boolean;
  onClose: () => void;
  onSelectConversation?: (conversationId: string) => void;
  openEventTrigger: () => void;
  selectedConversation?: Conversation;
}) {
  const { t } = useTranslation('intent-conversation-analytics');
  const [editingInstructionId, setEditingInstructionId] = useState(
    NO_SELECTED_INSTRUCTION,
  );
  const [feedbackPanel, setFeedbackPanel] = useState<{
    initialComment?: string | null;
    initialRating?: ConversationReviewRating;
    isOpen: boolean;
  }>({ isOpen: false });
  const [, { data: publishedInstruction, reset }] =
    usePublishInstructionMutation({
      fixedCacheKey: CONVERSATION_DRAWER_PUBLISHED_INSTRUCTION_CACHE_KEY,
    });
  const isOpen = !!selectedConversation?.conversation_id && !isLoading;
  const channel = selectedConversation?.channel;
  const isAuditDrawerEnabled = useIsAuditDrawerEnabled();

  const emitTrackingEventCallback = useEmitTrackingEventCallback();

  useEffect(() => {
    if (selectedConversation && !isLoading) {
      openEventTrigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitTrackingEventCallback, selectedConversation, isLoading]);

  const isSidebarOpen =
    editingInstructionId !== NO_SELECTED_INSTRUCTION ||
    Boolean(publishedInstruction) ||
    feedbackPanel.isOpen;

  // Flagged-queue navigation: locate the current conversation in the queue
  // and surface neighbour IDs for the feedback panel. When the current
  // conversation isn't in the queue (e.g. opened from a non-flagged row),
  // currentIndex is -1 so Previous stays disabled but Next steps into the
  // queue at the first flagged conversation — letting the user start a
  // review session from any row.
  const currentFlaggedIndex = selectedConversation
    ? flaggedConversationIds.indexOf(selectedConversation.conversation_id)
    : -1;
  const previousFlaggedId =
    currentFlaggedIndex > 0
      ? flaggedConversationIds[currentFlaggedIndex - 1]
      : undefined;
  // currentFlaggedIndex + 1 evaluates to 0 (the first queued conversation)
  // when the current row is not in the queue, and to the literal next item
  // otherwise — this single expression handles both the entry and the
  // mid-queue cases without branching.
  const nextFlaggedId =
    flaggedConversationIds[currentFlaggedIndex + 1] ?? undefined;
  // Navigate inside the panel: swap the selected conversation but keep the
  // panel mounted. Clear any prefill from the previous conversation so the
  // new panel doesn't inherit stale rating/comment values; the verdict card
  // on the new conversation will still show that conversation's saved review.
  const navigateToFlagged = (id: string) => {
    onSelectConversation?.(id);
    setFeedbackPanel({ isOpen: true });
  };

  return (
    <Drawer
      isOpen={isOpen || isLoading}
      onClose={() => {
        onClose();
        reset();
      }}
      width={isSidebarOpen ? '1336px' : '668px'}
    >
      {isLoading && (
        <Box
          sx={{
            p: '40px',
          }}
        >
          <Box
            sx={{
              py: '6px',
            }}
          >
            <Skeleton height='24px' width='100%' />
          </Box>
          <Box
            sx={{
              py: '20px',
            }}
          >
            <Skeleton height='400px' width='100%' />
          </Box>
        </Box>
      )}
      {isOpen &&
        (channel === 'voice' || channel === 'web_call' ? (
          <VoiceDrawerBody selectedConversation={selectedConversation} />
        ) : (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            {isAuditDrawerEnabled ? (
              <AuditDrawerBody
                onCreateNewInstruction={setEditingInstructionId}
                onOpenFeedbackPanel={(initialRating, initialComment) =>
                  setFeedbackPanel({
                    initialComment,
                    initialRating,
                    isOpen: true,
                  })
                }
                selectedConversation={selectedConversation}
              />
            ) : (
              <RevampDrawerBody
                onCreateNewInstruction={setEditingInstructionId}
                onOpenFeedbackPanel={(initialRating, initialComment) =>
                  setFeedbackPanel({
                    initialComment,
                    initialRating,
                    isOpen: true,
                  })
                }
                selectedConversation={selectedConversation}
              />
            )}
            {isSidebarOpen && (
              // Outer column stays stretched (default align-items) so its
              // borderLeft extends the full height of the drawer body —
              // matching the divider behaviour from before the sticky change.
              // The inner wrapper handles the sticky positioning so the
              // panel content stays in view while the left side scrolls.
              <Box
                sx={{
                  borderLeft: theme =>
                    '1px solid ' + theme.palette.colors.slate[200],
                }}
              >
                <Box sx={{ position: 'sticky', top: 0 }}>
                  {feedbackPanel.isOpen ? (
                    <FeedbackPanel
                      conversationId={selectedConversation.conversation_id}
                      initialComment={feedbackPanel.initialComment}
                      initialRating={feedbackPanel.initialRating}
                      key={selectedConversation.conversation_id}
                      nextConversationId={nextFlaggedId}
                      onClose={() => setFeedbackPanel({ isOpen: false })}
                      onSelectConversation={navigateToFlagged}
                      previousConversationId={previousFlaggedId}
                      queueLength={flaggedConversationIds.length}
                      queuePosition={
                        currentFlaggedIndex >= 0
                          ? currentFlaggedIndex + 1
                          : undefined
                      }
                    />
                  ) : (
                    <Box sx={{ p: '40px' }}>
                      {publishedInstruction ? (
                        <AddedInstruction
                          aiInstructionId={
                            publishedInstruction.ai_instruction_id
                          }
                          name={publishedInstruction.name}
                          reset={reset}
                        />
                      ) : (
                        <AiInstructionDetail
                          aiInstructionId={editingInstructionId}
                          FooterProps={{ marginTop: '24px' }}
                          header={
                            <Typography variant='font18Bold'>
                              {t('conversationDrawer.createKnowledgeCoaching')}
                            </Typography>
                          }
                          onClose={() =>
                            setEditingInstructionId(NO_SELECTED_INSTRUCTION)
                          }
                          publishCacheKey={
                            CONVERSATION_DRAWER_PUBLISHED_INSTRUCTION_CACHE_KEY
                          }
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        ))}
    </Drawer>
  );
}

const AddedInstruction = ({
  aiInstructionId,
  name,
  reset,
}: {
  aiInstructionId: string;
  name: string;
  reset: () => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '200px',
        width: '586px',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '360px',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            alt={t('addedInstruction.imageAlt')}
            src={DiscoverAutomationCheck}
          />
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            textAlign: 'center',
            width: '320px',
          }}
        >
          <Typography variant='font18Bold'>
            {t('addedInstruction.title', { name })}
          </Typography>
          <Typography color='grey.700' variant='font14'>
            {t('addedInstruction.savedMessage')}
          </Typography>
        </Box>
        <Button
          fullWidth
          onClick={() => {
            reset();
            navigate(`/solve/knowledge?aiInstruction=${aiInstructionId}`);
          }}
          variant='secondary'
        >
          {t('addedInstruction.viewInstructionButton')}
        </Button>
        <Button fullWidth onClick={reset} variant='ghost'>
          {t('addedInstruction.closeButton')}
        </Button>
      </Box>
    </Box>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- TODO: fix later
export const Card = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.colors.slate[200]};
  border-radius: 8px;
  padding: 16px;
`;

export const TextDivider = ({
  size,
  text,
}: {
  size: 'small' | 'large';
  text: string;
}) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        mb: size === 'large' ? 1 : 0,
        width: '100%',
      }}
    >
      <Divider />
      <Box
        sx={{
          px: '16px',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography variant={size === 'small' ? 'font12' : 'font16Bold'}>
          {text}
        </Typography>
      </Box>
      <Divider />
    </Box>
  );
};

export const InlineData = ({
  color,
  href,
  isLoading = false,
  label,
  value = NA,
}: {
  color?: string;
  href?: string;
  isLoading?: boolean;
  label: string;
  value?: string | number | ReactNode;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  const renderedValue = useMemo(() => {
    if (isLoading) {
      return <Skeleton width={30} />;
    }

    if (value === NA) {
      return (
        <Typography color={palette.colors.grey[500]} variant='font12Medium'>
          {value}
        </Typography>
      );
    }

    if (href) {
      return (
        <a href={href} rel='noreferrer' target='_blank'>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
            <Typography color={palette.colors.blue[600]} variant='font12Medium'>
              {value}
            </Typography>
            <IconExternalLink
              size={14}
              stroke={palette.colors.blue[600]}
              strokeWidth={'2px'}
            />
          </Box>
        </a>
      );
    }

    return value;
  }, [value, isLoading, href, palette.colors.blue, palette.colors.grey]);

  if (React.isValidElement(value)) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Typography color={palette.colors.grey[600]} variant='font12Medium'>
          {t('common.labelWithColon', { label })}
        </Typography>
        {value}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: href ? 'flex' : 'default',
        wordBreak: 'break-word',
      }}
    >
      <Typography color={palette.colors.grey[600]} variant='font12Medium'>
        {t('common.labelWithColon', { label })}
      </Typography>
      &nbsp;
      <Typography
        color={
          color ||
          (value === NA ? palette.colors.grey[500] : palette.colors.black)
        }
        variant='font12Medium'
      >
        {/* If loading, show a skeleton, otherwise either show the value or fallback to n/a */}
        {renderedValue}
      </Typography>
    </Box>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- TODO: fix later
export const Divider = styled('hr')`
  border-width: 0;
  border-bottom-width: thin;
  border-color: ${({ theme }) => theme.palette.colors.slate[200]};
  width: 100%;
`;
